import { useState, useMemo } from "react";
import { Filter, X, Building2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { BrokerageCard } from "@/components/BrokerageCard";
import { BrokerageFilters } from "@/components/brokerages/BrokerageFilters";
import { LocationPrompt } from "@/components/brokerages/LocationPrompt";
import { useBrokerages } from "@/hooks/useBrokerages";
import { useAgentLocation } from "@/hooks/useAgentLocation";

interface BrokerageFiltersType {
  state?: string;
  radius_miles?: number;
  best_for?: string[];
  brokerage_size?: string;
  brokerage_type?: string;
  priorities?: string[];
}

export default function BrokeragesBrowse() {
  const { location, setManualLocation, requestGeolocation, isLoading: isLoadingLocation } = useAgentLocation();
  const [filters, setFilters] = useState<BrokerageFiltersType>(() => ({
    state: location?.state || 'TX'
  }));
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { data, isLoading } = useBrokerages({
    state: filters.state || 'TX',
    radius_miles: filters.radius_miles,
    best_for: filters.best_for,
    priorities: filters.priorities,
    brokerage_size: filters.brokerage_size ? [filters.brokerage_size] : undefined,
    brokerage_type: filters.brokerage_type ? [filters.brokerage_type] : undefined,
  });
  
  const brokerages = data?.brokerages || [];

  const handleStateSelect = (state: string) => {
    setFilters(prev => ({ ...prev, state }));
    setManualLocation(state);
  };

  const handleUseMyLocation = async () => {
    await requestGeolocation();
    if (location?.state) {
      setFilters(prev => ({ ...prev, state: location.state }));
    }
  };

  const handleCompareToggle = (id: string) => {
    setCompareIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id) 
        : prev.length < 3 
          ? [...prev, id] 
          : prev
    );
  };

  const activeFilterCount = useMemo(() => {
    return [
      filters.best_for?.length || 0,
      filters.brokerage_size ? 1 : 0,
      filters.brokerage_type ? 1 : 0,
      filters.priorities?.length || 0,
    ].reduce((a, b) => a + b, 0);
  }, [filters]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary" />
              Find Your Brokerage
            </h1>
            <p className="text-muted-foreground mt-1">
              Compare brokerages and find the perfect fit for your career
            </p>
          </div>

          <div className="flex gap-6">
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-24 bg-card rounded-lg border p-4">
                <BrokerageFilters
                  filters={filters}
                  agentLocation={location}
                  onFiltersChange={setFilters}
                  onUseMyLocation={handleUseMyLocation}
                  isLoadingLocation={isLoadingLocation}
                />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="lg:hidden mb-4 flex items-center gap-2">
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Filter className="h-4 w-4" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4">
                      <BrokerageFilters
                        filters={filters}
                        agentLocation={location}
                        onFiltersChange={(f) => {
                          setFilters(f);
                          setMobileFiltersOpen(false);
                        }}
                        onUseMyLocation={handleUseMyLocation}
                        isLoadingLocation={isLoadingLocation}
                      />
                    </div>
                  </SheetContent>
                </Sheet>

                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({ state: filters.state })}
                    className="text-xs"
                  >
                    Clear filters
                    <X className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>

              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  {isLoading ? 'Loading...' : `${brokerages.length} brokerages found`}
                </p>
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="border rounded-lg p-4 space-y-3">
                      <div className="flex gap-4">
                        <Skeleton className="w-16 h-16 rounded-lg" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              ) : brokerages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {brokerages.map((brokerage) => (
                    <BrokerageCard
                      key={brokerage.id}
                      brokerage={brokerage}
                      isCompareSelected={compareIds.includes(brokerage.id!)}
                      onCompareToggle={handleCompareToggle}
                      compareDisabled={compareIds.length >= 3}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No brokerages found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or expanding your search area.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {compareIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg p-4 z-40">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">{compareIds.length} selected</span>
              <Button variant="ghost" size="sm" onClick={() => setCompareIds([])}>
                Clear
              </Button>
            </div>
            <Button disabled={compareIds.length < 2}>
              Compare Brokerages
            </Button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
